import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fabric } from 'fabric';
import { isMobile } from 'react-device-detect';
import type { Canvas } from 'fabric/fabric-impl';
import { ToastVariants, CUIColors } from '@dotcom/crown-ui';
import {
  DELETE_ICON_TOP_OFFSET,
  DELETE_ICON_TOP_OFFSET_LANDSCAPE,
  DELETE_ICON_TOP_OFFSET_PORTRAIT,
  TEXT_BOX_WIDTH_OFFSET_LANDSCAPE,
  TEXT_BOX_WIDTH_OFFSET_PORTRAIT,
} from '../constants/user-zone-textbox-offsets';
import { useAnalyticsContext } from '../context/analytics-context';
import { useAppContext } from '../context/app-context/app-context';
import { setIsImageUploadDrawerOpen, setIsToasterOpen } from '../context/app-context/app-context-actions';
import { useCardContext } from '../context/card-context';
import { useInitializationDataContext } from '../context/data-context';
import { usePhotoTextZoneControls } from '../features/card-editor/hooks/use-photo-text-zone-controls';
import { useTextArea } from '../features/card-editor/hooks/use-text-area';
import { useActiveCanvas, useCardFaceIsEditable, useFeatureFlags, useIsPodProductCode } from '../hooks';
import { ActionType, useDatadog } from '../hooks/use-datadog';
import { useIsOneToMany } from '../hooks/use-is-one-to-many';
import { config } from '../regional-config';
import colorsList from '../styles/util.scss';
import { CanvasDataTypes, helperSettingsConfig } from '../utils';
import { addUserTextHandlers, getObjectsByType, hasAvailablePhotoZones, photoZonesAreFilled } from '../utils/canvas';
import { getCardFaceClipPath } from '../utils/canvas-utils/get-card-face-clip-path';
import { createClipPathRect } from '../utils/canvas-utils/create-clip-path-rect';
import { convertPointToPixel } from '../utils/fonts';
import { ensureHiddenTextareaMinFont } from '../utils/ensure-hidden-textarea-min-font';
import { CardType } from '../global-types/card';
import { isSplitPanelFace, getPanelWidth } from '../utils/panel-zoom';
import { parseTextAction } from '../utils/parse-text-action';
import { getFontidByUrlName } from '../utils/utility/get-fontid-by-url-name';
import { getUserZoneTextboxOffsets } from '../utils/utility/get-user-zone-textbox-offsets';
import { getTextSettings, removeEditableAreaButtons } from '../components/card-editor/utils';

export type ToolActionHandlers = {
  handleTextClick: () => void;
  handleImageClick: () => void;
  handleWriteAMessageClick: () => void;
  handleTypeAMessageClick: () => void;
  shouldShowPodWam: boolean;
};

/**
 * Shared hook that provides toolbar/floating-menu action handlers.
 * Used by both the Toolbar component and the FloatingToolMenu component.
 */
export const useToolActions = (options?: {
  onToolbarItemClick?: (elementName: string) => void;
  checkOnCanvas?: boolean;
}): ToolActionHandlers => {
  const { onToolbarItemClick, checkOnCanvas = true } = options ?? {};
  const { appDispatch } = useAppContext();
  const { cardState } = useCardContext();
  const { updateEditFormats, firstElementSelected, trackPhotoUploadMethod } = useAnalyticsContext();
  const { initializedDataState } = useInitializationDataContext();
  const { isUK } = initializedDataState;
  const { scalingFactor } = helperSettingsConfig;
  const projectTypeCode = initializedDataState.data?.project_type_code;
  const { t } = useTranslation();
  const { IS_TEXTBOX_OVERLAY_ENABLED, IS_POD_USER_TEXTBOX_OVERLAY_ENABLED } = useFeatureFlags();
  const canvas = useActiveCanvas();
  const isPodProductCode = useIsPodProductCode();
  const { canAddText, type: faceType, isEditable, canAddPhoto } = useCardFaceIsEditable();
  const isOneToMany = useIsOneToMany();
  const { addCustomTextArea } = useTextArea();
  const { handleTypeAMessage, handleAddHandwriting } = usePhotoTextZoneControls();
  const { features } = config;
  const currentCardFace = cardState.cardFacesList[cardState.activeCardIndex];
  const shouldShowPodWam = !!(
    ((currentCardFace?.type !== 'front' && features?.isPodWamEnabledOnlyInside) || features?.isPodWamEnabled) &&
    !isOneToMany
  );

  const orientation = cardState.cardFormat;
  const isPortrait = orientation === 'portrait';
  const isLandscape = orientation === 'landscape';

  const { textAction } = useDatadog();

  const openNonEditableToast = useCallback(
    () =>
      setIsToasterOpen(appDispatch, {
        title: '',
        children: faceType ? t('nonEditableMessage.message', { face: faceType }) : t('nonEditableMessage.fallback'),
        variant: ToastVariants.Error,
      }),
    [t, faceType],
  );

  const openCoverIsFullToast = useCallback(
    () =>
      setIsToasterOpen(appDispatch, {
        title: '',
        children: t('nonEditableMessage.coverIsFull'),
        variant: ToastVariants.Error,
      }),
    [],
  );

  const handleTextClick = useCallback(() => {
    if (!canAddText) {
      openNonEditableToast();
      return;
    }

    if (canvas && canvas.current) {
      if (onToolbarItemClick) {
        onToolbarItemClick('text');
      }

      if (IS_TEXTBOX_OVERLAY_ENABLED && IS_POD_USER_TEXTBOX_OVERLAY_ENABLED) {
        const defaultTextAreaSettings = { ...initializedDataState.data?.variables.template_data.DefaultTextArea };
        const INITIAL_TEXT_MESSAGE = defaultTextAreaSettings?.Text || `${t('cardEditor.userTextDefaultMessage')}`;
        const currentCanvas = canvas.current as fabric.Canvas;
        const lastTextBox = getObjectsByType(currentCanvas, CanvasDataTypes.UserText).at(-1) as fabric.Textbox;

        if (lastTextBox?.text === INITIAL_TEXT_MESSAGE) {
          canvas.current.setActiveObject(lastTextBox);
          lastTextBox.enterEditing();
          canvas.current.renderAll();
          textAction(ActionType.ADD_TEXT, parseTextAction(lastTextBox));
          return;
        }

        addCustomTextArea({
          canvas,
          cardFace: currentCardFace,
          onError: () => {
            throw new Error('Error adding custom text area');
          },
        });

        const newTextBox = canvas.current.getActiveObject() as fabric.Textbox | null;
        newTextBox?.enterEditing?.();
      } else {
        const defaultTextAreaSettings = { ...initializedDataState.data?.variables.template_data.DefaultTextArea };
        const INITIAL_TEXT_MESSAGE = defaultTextAreaSettings?.Text || `${t('cardEditor.userTextDefaultMessage')}`;
        const textBoxes = canvas.current as fabric.Canvas;
        const cardFaceClipPath = getCardFaceClipPath(cardState.cardFacesList[cardState.activeCardIndex], 0);
        const lastTextBox = getObjectsByType(textBoxes, CanvasDataTypes.UserText).at(-1) as fabric.Textbox;
        const canvasWidth = canvas.current.getWidth();
        const canvasZoom = canvas.current.getZoom();
        const activeCardFace = cardState.cardFacesList[`${cardState.activeCardIndex}`] ?? {};
        const cardFaceType = activeCardFace.type?.split('-')[0];

        if (onToolbarItemClick) {
          onToolbarItemClick('text');
        }

        if (lastTextBox?.text === INITIAL_TEXT_MESSAGE) {
          canvas.current.setActiveObject(lastTextBox);
          if (isMobile) {
            const handleEditingEntered = () => {
              lastTextBox.off('editing:entered', handleEditingEntered);
              ensureHiddenTextareaMinFont(lastTextBox.hiddenTextarea);
            };
            lastTextBox.on('editing:entered', handleEditingEntered);
          }
          lastTextBox.enterEditing();
          canvas.current.renderAll();
          textAction(ActionType.ADD_TEXT, parseTextAction(lastTextBox));
          return;
        }

        const defaultFallbackColor = isUK ? colorsList[CUIColors.CharcoalGrey] : colorsList[CUIColors.Black];
        const defaultColor = defaultTextAreaSettings?.TextColor || defaultFallbackColor;
        const defaultFontSize = convertPointToPixel(
          initializedDataState.data?.variables.template_data.DefaultTextArea.FontSize || 50,
        );
        defaultTextAreaSettings.FontFamilyId = getFontidByUrlName(
          defaultTextAreaSettings?.FontFamily || '',
          initializedDataState.data?.font_collection.fonts,
        );
        const defaultTextSettings = getTextSettings({
          cardState,
          color: defaultColor,
          defaultFontSize,
          isUK,
          defaultOptions: defaultTextAreaSettings,
          name: CanvasDataTypes.UserText,
        });

        const isPanelZoom =
          isPortrait &&
          (projectTypeCode === CardType.POD || projectTypeCode === CardType.DG) &&
          isSplitPanelFace(activeCardFace.type);
        const isRightPanel = isPanelZoom && activeCardFace.type === 'inside-right';

        const landscapeLeftMargin = 65;
        const portraitLeftMargin = 52;
        const rightPanelLeftOffset = canvasWidth / canvasZoom + portraitLeftMargin;
        const leftMargin = isRightPanel ? rightPanelLeftOffset : portraitLeftMargin;
        defaultTextSettings.left = isLandscape ? landscapeLeftMargin : leftMargin;

        const horizontalPadding = 20;
        const totalPadding = 2 * (horizontalPadding * scalingFactor * 2);
        const panelWidth = getPanelWidth({ isPanelZoom, canvasWidth, canvasZoom });
        const width =
          isLandscape || cardFaceType === 'front' || cardFaceType === 'back'
            ? canvasWidth / canvasZoom - totalPadding
            : panelWidth - totalPadding;

        const defaultTextboxHeight = 100;
        const textElement = new fabric.Textbox(INITIAL_TEXT_MESSAGE, {
          top: isPortrait ? DELETE_ICON_TOP_OFFSET_PORTRAIT : DELETE_ICON_TOP_OFFSET_LANDSCAPE,
          width: isPortrait ? width - TEXT_BOX_WIDTH_OFFSET_PORTRAIT : width - TEXT_BOX_WIDTH_OFFSET_LANDSCAPE,
          height: defaultTextboxHeight,
          hasControls: true,
          borderColor: isUK ? '#333333' : '#604099',
          ...defaultTextSettings,
        });
        textElement.set({ data: { type: CanvasDataTypes.UserText } });

        if (cardFaceClipPath) {
          const { TOP_OFFSET, LEFT_OFFSET, RIGHT_OFFSET } = getUserZoneTextboxOffsets(cardState.cardFormat);
          textElement.set({
            top: cardFaceClipPath.top + DELETE_ICON_TOP_OFFSET + TOP_OFFSET,
            left: cardFaceClipPath.left + LEFT_OFFSET,
            width: cardFaceClipPath.width - (LEFT_OFFSET + RIGHT_OFFSET),
          });
          textElement.clipPath = createClipPathRect(cardFaceClipPath);
        }

        addUserTextHandlers(textElement, INITIAL_TEXT_MESSAGE, defaultColor, canvas.current);
        removeEditableAreaButtons(canvas.current);
        canvas.current.add(textElement);
        canvas.current.setActiveObject(textElement);
        if (isMobile) {
          textElement.on('editing:entered', () => {
            ensureHiddenTextareaMinFont(textElement.hiddenTextarea);
          });
        }
        textElement.enterEditing();
      }
    }

    updateEditFormats({ eventAction: !firstElementSelected ? 'start' : 'add' });
  }, [canvas, canAddText, currentCardFace, cardState, IS_TEXTBOX_OVERLAY_ENABLED, IS_POD_USER_TEXTBOX_OVERLAY_ENABLED]);

  const openPhotoUploadDrawer = useCallback(() => {
    if (onToolbarItemClick) {
      onToolbarItemClick('photo');
    }
    setIsImageUploadDrawerOpen(appDispatch);
  }, [appDispatch, onToolbarItemClick]);

  const handleImageClick = useCallback(() => {
    trackPhotoUploadMethod('photo object');
    const currentCanvas = canvas?.current as Canvas;

    if (faceType === 'front' && checkOnCanvas && hasAvailablePhotoZones(currentCanvas)) {
      return openPhotoUploadDrawer();
    }

    if (faceType === 'front' && photoZonesAreFilled(currentCanvas)) {
      return openCoverIsFullToast();
    }

    if (!isEditable || !canAddPhoto) {
      openNonEditableToast();
      return;
    }

    openPhotoUploadDrawer();
  }, [canvas, faceType, isEditable, canAddPhoto, checkOnCanvas]);

  const handleWriteAMessageClick = useCallback(() => {
    if ((!canAddPhoto || !canAddText) && isPodProductCode) {
      openNonEditableToast();
      return;
    }

    if (canvas?.current) {
      handleAddHandwriting({
        canvas: canvas.current,
      });
    }
  }, [canvas, canAddPhoto, canAddText, isPodProductCode]);

  const handleTypeAMessageClick = useCallback(() => {
    if (canvas?.current) {
      handleTypeAMessage({
        canvas: canvas.current,
      });
    }
  }, [canvas]);

  return {
    handleTextClick,
    handleImageClick,
    handleWriteAMessageClick,
    handleTypeAMessageClick,
    shouldShowPodWam,
  };
};
